'use client';

import type { CSSProperties } from 'react';

import { buildEmbedConfig } from './embed/buildEmbedConfig';
import { useWidgetConfig } from './hooks/useWidgetConfig';
import { WidgetConfigPanel } from './panel/WidgetConfigPanel';
import { DataSourceBlock } from './preview/DataSourceBlock';
import { EmbedCodeBlock } from './preview/EmbedCodeBlock';
import { WidgetPreview } from './preview/WidgetPreview';
import type { WidgetSchema } from './types';

// Stage height per widget size when the widget itself has no fixed px height.
const STAGE_HEIGHT: Record<string, number> = { large: 420, medium: 300, small: 220, inline: 220, ticker: 60 };

/**
 * The configure / preview / copy experience. Config and preview sit side by side
 * (stacking on narrow screens); the embed code spans full width below. The widget
 * renders at its true dimensions on a recessed surface so its real edges read
 * clearly without being nested in gallery chrome.
 */
export function WidgetWorkbench({ schema }: { schema: WidgetSchema }) {
    const { config, setField } = useWidgetConfig(schema);
    const embed = buildEmbedConfig(schema, config);
    const widgetSize = schema.embed.fixed.widgetSize ?? 'large';
    const isInline = widgetSize === 'inline' || widgetSize === 'small';

    const pxHeight = /^(\d+)px$/.exec(embed.height);
    const pxWidth = /^(\d+)px$/.exec(embed.width);
    const stageHeight = pxHeight ? Number(pxHeight[1]) : (STAGE_HEIGHT[widgetSize] ?? 420);
    // The watchlist (medium) fits the embedded widget's real content height (measured
    // in EmbedScriptFrame) instead of a fixed frame, so a short list isn't padded out.
    const isMedium = widgetSize === 'medium';

    // Fixed-size widgets (px width AND height, e.g. the 16:9 broadcast view) preview at their
    // intrinsic aspect ratio, so capping the width to the pane scales the height with it instead
    // of letterboxing inside a fixed-height frame. Others keep the fixed-height stage.
    let frameStyle: CSSProperties;
    if (isMedium) {
        frameStyle = { width: embed.width, maxWidth: '100%', minHeight: 120 };
    } else if (pxWidth && pxHeight) {
        frameStyle = { width: embed.width, maxWidth: '100%', aspectRatio: `${pxWidth[1]} / ${pxHeight[1]}` };
    } else {
        frameStyle = { width: embed.width, maxWidth: '100%', height: stageHeight };
    }
    // Mirror the embed's optional frame chrome (Border / Rounded toggles) in the preview.
    const previewFrame = [
        'overflow-hidden shadow-x10',
        embed.showBorder ? 'border border-solid border-stroke-x30' : '',
        embed.rounded ? 'rounded-[12px]' : '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div className="space-y-10">
            <div className="grid grid-cols-1 gap-x-8 gap-y-8 wlg:grid-cols-[minmax(0,340px)_minmax(0,1fr)]">
                <aside>
                    <p className="typ-overline-x10 mb-4 text-text-x30">Configure</p>
                    <div className="rounded-x40 border border-stroke-x30 bg-surface-x10 p-5 shadow-x10">
                        <WidgetConfigPanel schema={schema} config={config} setField={setField} />
                    </div>
                </aside>

                <section className="min-w-0">
                    <div className="mb-4 flex items-baseline justify-between">
                        <p className="typ-overline-x10 text-text-x30">Live preview</p>
                        <span className="font-mono text-[11px] tabular-nums text-text-x30">
                            {embed.width} × {embed.height} · {embed.colorTheme}
                        </span>
                    </div>

                    {/* No wrapping card/backdrop: the preview is the raw embed exactly as a
                        partner would see it on their page. */}
                    {isInline ? (
                        <div className="flex min-h-[120px] items-center justify-center">
                            <WidgetPreview schema={schema} config={config} />
                        </div>
                    ) : (
                        <div className="flex justify-center">
                            {/* Widgets are edge-to-edge; the embedder adds corners/chrome. The
                                preview just lifts the raw widget off the page with a soft shadow. */}
                            <div className={previewFrame} style={frameStyle}>
                                <WidgetPreview schema={schema} config={config} />
                            </div>
                        </div>
                    )}
                </section>
            </div>

            <EmbedCodeBlock schema={schema} config={config} />

            <DataSourceBlock schema={schema} config={config} />
        </div>
    );
}
